/** Положение элемента в видимой части области прокрутки после прокрутки. */
export type ScrollAlign = 'start' | 'center' | 'end';

type ScrollOptions = {
  align?: ScrollAlign;
  behavior?: ScrollBehavior;
  /**
   * Область прокрутки, которую двигаем. Без неё берётся ближайший прокручиваемый предок —
   * но он определяется по стилю, а тот у области с отложенной полосой прокрутки становится
   * прокручиваемым не сразу.
   */
  container?: HTMLElement | null;
  /**
   * Зазор между верхним краем области прокрутки и элементом, px: элемент встаёт не вплотную к краю.
   * Учитывается только при выравнивании по верхнему краю (`start`) — том случае, ради которого зазор
   * и заводился. Другим выравниваниям он ничего не даёт: у `center` края нет вовсе, а зазору у
   * нижнего края нет ни одного потребителя, по которому знак отступа был бы проверен.
   */
  gap?: number;
};

/** Ближайший предок, который реально прокручивается по вертикали. */
const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (isScrollable && parent.scrollHeight > parent.clientHeight) return parent;
    parent = parent.parentElement;
  }
  return null;
};

/**
 * Положение прокрутки области, при котором элемент встаёт по `align`, — в пределах, до которых
 * область вообще прокручивается. Не зависит от текущей прокрутки, в том числе посреди плавной.
 */
const getTargetScrollTop = (
  element: HTMLElement,
  container: HTMLElement,
  { align = 'start', gap = 0 }: Pick<ScrollOptions, 'align' | 'gap'>,
): number => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const offset = elementRect.top - containerRect.top + container.scrollTop;

  let top = offset - gap;
  if (align === 'center') top = offset - container.clientHeight / 2 + elementRect.height / 2;
  if (align === 'end') top = offset - container.clientHeight + elementRect.height;

  return Math.max(0, Math.min(top, container.scrollHeight - container.clientHeight));
};

/**
 * Прокручивает элемент в видимую часть его собственной области прокрутки.
 *
 * Отличие от `scrollIntoView`: двигается только ближайшая прокручиваемая область,
 * внешние области и страница остаются на месте. Иначе прокрутка списка к последнему
 * элементу утаскивает вниз всю страницу вместе с шапкой приложения.
 */
export const scrollIntoViewWithinContainer = (
  element: HTMLElement | null | undefined,
  { behavior = 'auto', container: explicitContainer, ...placement }: ScrollOptions = {},
): void => {
  if (!element) return;

  const container = explicitContainer ?? findScrollableParent(element);
  if (!container) return;

  container.scrollTo({ top: getTargetScrollTop(element, container, placement), behavior });
};

/** Сколько держать элемент на месте после перехода к нему, мс, если пользователь не взялся за прокрутку сам. */
const FOLLOW_DURATION_MS = 5000;

/** Действия, которыми пользователь сам берётся за прокрутку области — удержание им не мешает. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/**
 * Прокручивает элемент в видимую часть его области прокрутки, как {@link scrollIntoViewWithinContainer},
 * и держит его там, пока раскладка области меняется: догружаются картинки над ним — и элемент уезжает
 * вниз уже во время прокрутки, а она останавливается у прежней границы.
 *
 * Удержание снимается, когда пользователь сам берётся за прокрутку, по истечении {@link FOLLOW_DURATION_MS}
 * или вызовом возвращённой функции — её зовут, переходя к другому элементу.
 */
export const followIntoViewWithinContainer = (
  element: HTMLElement | null | undefined,
  { behavior = 'auto', container: explicitContainer, ...placement }: ScrollOptions = {},
): (() => void) => {
  const container = element ? (explicitContainer ?? findScrollableParent(element)) : null;
  if (!element || !container) {
    return () => {
      // Удерживать нечего — снимать тоже
    };
  }

  let scrolledTop = getTargetScrollTop(element, container, placement);
  container.scrollTo({ top: scrolledTop, behavior });

  // Прокрутка повторяется, только когда цель сдвинулась: наблюдатель сообщает и о первом замере
  const observer = new ResizeObserver(() => {
    const top = getTargetScrollTop(element, container, placement);
    if (Math.abs(top - scrolledTop) < 1) return;
    scrolledTop = top;
    container.scrollTo({ top, behavior });
  });
  // Сама область и её содержимое: высоту ленты меняет содержимое, а не область
  observer.observe(container);
  for (const child of container.children) observer.observe(child);

  // Полоса прокрутки может лежать рядом с областью, а не внутри неё — слушается их общий родитель
  const interactionTarget = container.parentElement ?? container;
  const stop = () => {
    observer.disconnect();
    clearTimeout(timerId);
    for (const type of USER_SCROLL_EVENTS) interactionTarget.removeEventListener(type, stop);
  };
  const timerId = setTimeout(stop, FOLLOW_DURATION_MS);
  for (const type of USER_SCROLL_EVENTS) interactionTarget.addEventListener(type, stop, { passive: true });

  return stop;
};
